/**
 * One message of the conversation (docs/plans/lms-course-editor-plan.md 8.3,
 * 6.10 to 6.13, 18.3 to 18.5; WP-E30): the admin's words, name and time, the
 * scope, a status pill with its word (the state rule of editModel.ts: a NULL
 * outcome, or 'failed' while a retried run works again, follows run.status),
 * and then, by state:
 *
 * - reading the documents: the files with their extraction outcome (once a
 *   read returned them), the progress line and Cancel;
 * - ready: "Ready: up to $x" with Send (the second job) and Discard;
 * - working: the progress message, the spend so far and Cancel (a confirm);
 * - done: the planner's reply, the "Changed:" line, the cost (estimate and
 *   actual), the warnings (unknown kinds in generic words, 18.5), and View
 *   changes (the per-message diff in a Modal), Accept and Revert (a confirm
 *   with an optional reason; a stale revert shows 6.11's copy);
 * - failed: the cause and the stage, and "Try again" only when the run block
 *   says retryable (18.5). A message that met a changed draft shows no diff
 *   (18.5, 6.9 step 1 narrowed).
 *
 * Send, Try again and Revert first call onBeforeSend (the editor flushes its
 * autosave; a failed save stops the action), so a hand edit is on the server
 * before any hash is compared (G24). A read-only card (a published version's
 * history, 8.6) shows everything and offers no action but View changes.
 */
import { useState } from 'react';
import { AlertTriangle, Check, Eye, Loader2, RotateCcw, Send, X } from 'lucide-react';
import ConfirmModal from '../../../../../ui/ConfirmModal';
import Modal from '../../../../../ui/Modal';
import { fieldCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../../ui/styles';
import { useToast } from '../../../../../ui/Toast';
import { centsOf } from '../../../../api/builder';
import type { BuilderSource } from '../../../../api/builder';
import {
  acceptEditMessage,
  cancelEditMessage,
  EDITOR_REVERT_REASON_MAX,
  getEditMessage,
  readEditorError,
  revertEditMessage,
  startEditMessage,
} from '../../../../api/editor';
import type { EditDiff, EditMessage, EditRun } from '../../../../api/editor';
import { noticeCls, pillCls } from '../../../../components/player/styles';
import { dollars, fileSizeLabel } from '../builder/runModel';
import { hintCls, labelCls, microLabelCls } from '../styles';
import { EditDiffView } from './EditDiffView';
import {
  changedLine,
  changeNoteOf,
  costLine,
  DRAFT_CHANGED_COPY,
  explainEditError,
  extractionWord,
  formatDateTime,
  isPolledMessage,
  messageState,
  scopeLine,
  stageWords,
  statePill,
  warningLines,
} from './editModel';
import type { EditSectionRef } from './editModel';

/** What an action changed, for the panel (8.3's onChanged). */
export interface EditMessageChange {
  message: EditMessage;
  run?: EditRun | null;
  /** true when the draft changed on the server (a revert): the editor reloads it. */
  draftChanged?: boolean;
}

export interface EditMessageCardProps {
  message: EditMessage;
  /** The builder's run summary, once read (for cancel_requested_at); the message's run block is the fallback. */
  run?: EditRun | null;
  /** The attachments, once read. */
  sources?: BuilderSource[] | null;
  /** The draft's sections, for "section 4" in the copy. */
  sections: EditSectionRef[];
  /** A published version's history (8.6): nothing but View changes. */
  readOnly?: boolean;
  /** The draft is read-only (a run is working): Send, Try again and Revert wait. */
  locked?: boolean;
  /** Flush the editor's autosave; false stops the action. */
  onBeforeSend: () => Promise<boolean>;
  onChanged: (change: EditMessageChange) => void;
}

type Dialog = 'cancel' | 'discard' | 'revert' | 'diff' | null;
type Busy = 'cancel' | 'send' | 'accept' | 'revert' | null;

const SAVE_FAILED = 'The draft could not be saved first, so nothing was sent. Check the editor and try again.';

export function EditMessageCard({
  message,
  run,
  sources,
  sections,
  readOnly = false,
  locked = false,
  onBeforeSend,
  onChanged,
}: EditMessageCardProps) {
  const { toast } = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [revertError, setRevertError] = useState<string | null>(null);
  const [diff, setDiff] = useState<EditDiff | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const state = messageState(message);
  const pill = statePill(message);
  const block = message.run;
  const working = isPolledMessage(message);
  const applied = message.outcome === 'applied';
  const who = message.requested_by && message.requested_by.full_name ? message.requested_by.full_name : 'An admin';
  const cancelRequested = Boolean(run && run.cancel_requested_at);
  const changed = changedLine(message, sections);
  const note = changeNoteOf(message);
  const cost = costLine(message);
  const warnings = warningLines(message.warnings, sections);
  const errorCode = block ? block.error_code : null;
  const canAct = !readOnly;

  const withSave = async (action: () => Promise<void>) => {
    let saved = false;
    try {
      saved = await onBeforeSend();
    } catch {
      saved = false;
    }
    if (!saved) {
      setError(SAVE_FAILED);
      return false;
    }
    await action();
    return true;
  };

  const start = async () => {
    setError(null);
    setBusy('send');
    try {
      await withSave(async () => {
        const res = await startEditMessage(message.id);
        onChanged({ message: res.message, run: res.run });
      });
    } catch (err) {
      const problem = readEditorError(err);
      setError(explainEditError(problem.code, problem.message));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    setError(null);
    setBusy('cancel');
    try {
      const res = await cancelEditMessage(message.id);
      onChanged({ message: res.message, run: res.run });
      setDialog(null);
      toast(
        res.run && res.run.status === 'cancelled' ? `Message ${message.ordinal} was cancelled.` : `Cancel requested for message ${message.ordinal}; it stops before its next step.`,
        { type: 'info' }
      );
    } catch (err) {
      const problem = readEditorError(err);
      setError(explainEditError(problem.code, problem.message));
      setDialog(null);
    } finally {
      setBusy(null);
    }
  };

  const accept = async () => {
    setError(null);
    setBusy('accept');
    try {
      const next = await acceptEditMessage(message.id);
      onChanged({ message: next });
      toast(`Message ${message.ordinal} accepted.`, { type: 'success' });
    } catch (err) {
      const problem = readEditorError(err);
      setError(explainEditError(problem.code, problem.message));
    } finally {
      setBusy(null);
    }
  };

  const revert = async () => {
    setRevertError(null);
    setBusy('revert');
    try {
      let saved = false;
      try {
        saved = await onBeforeSend();
      } catch {
        saved = false;
      }
      if (!saved) {
        setRevertError(SAVE_FAILED);
        return;
      }
      const res = await revertEditMessage(message.id, reason);
      setDialog(null);
      setReason('');
      onChanged({ message: res.message, draftChanged: true });
      toast(`Message ${message.ordinal} reverted: the draft is back to how it was before it.`, { type: 'success' });
    } catch (err) {
      const problem = readEditorError(err);
      setRevertError(explainEditError(problem.code, problem.message));
    } finally {
      setBusy(null);
    }
  };

  const openDiff = async () => {
    setDialog('diff');
    setDiffError(null);
    if (diff) return;
    setDiffLoading(true);
    try {
      const detail = await getEditMessage(message.id);
      if (detail.diff) setDiff(detail.diff);
      else setDiffError('No changes were recorded for this message.');
    } catch (err) {
      const problem = readEditorError(err, 'The changes could not be loaded.');
      setDiffError(explainEditError(problem.code, problem.message, 'The changes could not be loaded.'));
    } finally {
      setDiffLoading(false);
    }
  };

  const failureText = (() => {
    if (state !== 'failed') return null;
    if (errorCode === 'LMS_EDITOR_DRAFT_CHANGED') return DRAFT_CHANGED_COPY;
    const where = block ? stageWords(block.stage) : null;
    const copy = explainEditError(errorCode, block ? block.error : null, 'The message stopped on an unexpected error.');
    return where ? `${copy} It stopped ${where}.` : copy;
  })();

  const retryable = Boolean(block && block.retryable === true);
  const reasonTooLong = reason.trim().length > EDITOR_REVERT_REASON_MAX;

  return (
    <li
      className="space-y-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
      data-testid="lms-edit-message"
      data-message-id={message.id}
      data-state={state}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-semibold text-gray-700 dark:text-gray-200">{`Message ${message.ordinal}`}</span>
          {` \u00b7 ${who}`}
          {message.created_at ? ` \u00b7 ${formatDateTime(message.created_at)}` : ''}
        </div>
        <span className={pillCls(pill.tone)} data-testid="lms-edit-message-status" aria-live="polite">
          {pill.label}
        </span>
      </div>

      <p className="whitespace-pre-wrap break-words text-sm text-gray-900 dark:text-gray-100" data-testid="lms-edit-message-text">
        {message.instruction}
      </p>
      <p className={hintCls} data-testid="lms-edit-message-scope">
        {`Scope: ${scopeLine(message.scope, sections)}${message.has_material ? ' \u00b7 with documents' : ''}`}
      </p>

      {message.has_material && Array.isArray(sources) && sources.length > 0 ? (
        <ul className="space-y-1" data-testid="lms-edit-message-sources">
          {sources.map((s) => {
            const word = extractionWord(s);
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                <span className="min-w-0 truncate font-medium">{s.title || s.filename}</span>
                {s.size_bytes ? <span className="text-gray-500 dark:text-gray-400">{fileSizeLabel(s.size_bytes)}</span> : null}
                <span className={pillCls(word.tone)}>{word.label}</span>
                {s.extraction_note ? <span className="text-gray-500 dark:text-gray-400">{s.extraction_note}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {working ? (
        <div className="space-y-1" data-testid="lms-edit-message-progress">
          {block && block.progress && block.progress.message ? (
            <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
              {block.progress.message}
            </p>
          ) : (
            <p className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
              Working on this message...
            </p>
          )}
          <p className={hintCls} data-testid="lms-edit-message-spent">
            {`Spent so far ${dollars(block ? centsOf(block.spent_cents) : 0)}${block && block.estimate_cents !== null ? ` of up to ${dollars(block.estimate_cents)}` : ''}`}
          </p>
          {cancelRequested ? <p className={hintCls}>Cancel requested: it stops before its next step.</p> : null}
        </div>
      ) : null}

      {state === 'ready' ? (
        <div className={noticeCls('amber')} data-testid="lms-edit-message-ready">
          <p>{`Ready: up to ${dollars(block ? block.estimate_cents : 0)}. The documents are read; send the message to run it, or discard it.`}</p>
        </div>
      ) : null}

      {message.reply && !working ? (
        state === 'clarify' ? (
          <div className={noticeCls('amber')} data-testid="lms-edit-message-reply">
            <div>
              <p className="font-medium">{message.reply}</p>
              <p className="mt-1 text-xs">Answer with a new message below; nothing was changed.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <div className={microLabelCls}>Reply</div>
            <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200" data-testid="lms-edit-message-reply">
              {message.reply}
            </p>
          </div>
        )
      ) : null}

      {changed ? (
        <p className="text-sm text-gray-800 dark:text-gray-200" data-testid="lms-edit-message-changed">
          {changed}
        </p>
      ) : null}
      {note && applied ? (
        <p className={hintCls} data-testid="lms-edit-message-note">
          {`Change note: ${note}`}
        </p>
      ) : null}
      {cost && !working ? (
        <p className={`${hintCls} tabular-nums`} data-testid="lms-edit-message-cost">
          {cost}
        </p>
      ) : null}

      {warnings.length > 0 ? (
        <ul className="space-y-1" data-testid="lms-edit-message-warnings">
          {warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300" data-testid="lms-edit-message-warning">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {failureText ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-edit-message-failure">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <p>{failureText}</p>
        </div>
      ) : null}

      {message.review === 'accepted' || message.review === 'reverted' ? (
        <p className={hintCls} data-testid="lms-edit-message-review">
          {`${message.review === 'accepted' ? 'Accepted' : 'Reverted'}${message.reviewed_by && message.reviewed_by.full_name ? ` by ${message.reviewed_by.full_name}` : ''}${message.reviewed_at ? ` on ${formatDateTime(message.reviewed_at)}` : ''}${message.review === 'reverted' && message.revert_reason ? `. Reason: ${message.revert_reason}` : ''}`}
        </p>
      ) : null}

      {error ? (
        <p className="text-xs text-red-700 dark:text-red-400" role="alert" data-testid="lms-edit-message-error">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {applied ? (
          <button type="button" className={outlineBtnCls()} onClick={openDiff} data-testid="lms-edit-view-changes">
            <Eye className="h-4 w-4" aria-hidden="true" />
            View changes
          </button>
        ) : null}
        {canAct && applied && message.review === 'pending' ? (
          <button type="button" className={primaryBtnCls} onClick={accept} disabled={busy !== null} data-testid="lms-edit-accept">
            {busy === 'accept' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
            Accept
          </button>
        ) : null}
        {canAct && applied && (message.review === 'pending' || message.review === 'accepted') ? (
          <button
            type="button"
            className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
            onClick={() => {
              setRevertError(null);
              setDialog('revert');
            }}
            disabled={busy !== null || locked}
            title={locked ? 'The draft is read-only while a run is working.' : undefined}
            data-testid="lms-edit-revert"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Revert
          </button>
        ) : null}
        {canAct && state === 'ready' ? (
          <>
            <button type="button" className={primaryBtnCls} onClick={start} disabled={busy !== null || locked} data-testid="lms-edit-start">
              {busy === 'send' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
              Send
            </button>
            <button
              type="button"
              className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
              onClick={() => setDialog('discard')}
              disabled={busy !== null}
              data-testid="lms-edit-discard"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Discard
            </button>
          </>
        ) : null}
        {canAct && working && !cancelRequested ? (
          <button
            type="button"
            className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
            onClick={() => setDialog('cancel')}
            disabled={busy !== null}
            data-testid="lms-edit-cancel"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Cancel
          </button>
        ) : null}
        {canAct && state === 'failed' && retryable ? (
          <button type="button" className={primaryBtnCls} onClick={start} disabled={busy !== null || locked} data-testid="lms-edit-retry">
            {busy === 'send' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
            Try again
          </button>
        ) : null}
      </div>

      <ConfirmModal
        isOpen={dialog === 'cancel' || dialog === 'discard'}
        onClose={() => setDialog(null)}
        onConfirm={cancel}
        title={dialog === 'discard' ? `Discard message ${message.ordinal}?` : `Cancel message ${message.ordinal}?`}
        message={
          dialog === 'discard'
            ? 'The message will not run and nothing more is spent. Its documents stay on record.'
            : 'The message stops before its next step. Nothing is written to the draft unless it was already saving; what was spent so far stays spent.'
        }
        confirmLabel={dialog === 'discard' ? 'Discard' : 'Cancel the message'}
        cancelLabel="Keep it"
        danger
        loading={busy === 'cancel'}
      />

      <ConfirmModal
        isOpen={dialog === 'revert'}
        onClose={() => {
          if (busy !== 'revert') setDialog(null);
        }}
        onConfirm={() => {
          if (!reasonTooLong) void revert();
        }}
        title={`Revert message ${message.ordinal}?`}
        message={
          <div className="space-y-2">
            <p>The draft goes back exactly to how it was before this message. The message and its changes stay on record.</p>
            <label className={labelCls} htmlFor={`lms-edit-reason-${message.id}`}>
              Reason (optional)
            </label>
            <textarea
              id={`lms-edit-reason-${message.id}`}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={`${fieldCls} w-full`}
              data-testid="lms-edit-revert-reason"
            />
            {reasonTooLong ? (
              <p className="text-xs text-red-700 dark:text-red-400">{`The reason is longer than ${EDITOR_REVERT_REASON_MAX} characters.`}</p>
            ) : null}
            {revertError ? (
              <p className="text-xs text-red-700 dark:text-red-400" role="alert" data-testid="lms-edit-revert-error">
                {revertError}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Revert"
        danger
        loading={busy === 'revert'}
      />

      <Modal isOpen={dialog === 'diff'} onClose={() => setDialog(null)} title={`Changes made by message ${message.ordinal}`} maxWidth="max-w-4xl">
        {diffLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400" role="status">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Loading the changes...
          </div>
        ) : diffError ? (
          <div className={noticeCls('red')} role="alert" data-testid="lms-edit-diff-error">
            {diffError}
          </div>
        ) : diff ? (
          <EditDiffView diff={diff} />
        ) : null}
      </Modal>
    </li>
  );
}

export default EditMessageCard;
